"use client";

import { ColumnDef } from "@tanstack/react-table";
import { IUnifiedCampaign } from "@/@types/campaign";
import { CampaignTypeBadge } from "./campaign-type-badge";
import { CampaignStatusBadge } from "./campaign-status-badge";
import { UnifiedCampaignRowActions } from "./unified-campaign-row-actions";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowUpDown, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface GetColumnsProps {
  onViewDetails: (campaign: IUnifiedCampaign) => void;
  onEdit?: (campaign: IUnifiedCampaign) => void;
  onViewProducts?: (campaign: IUnifiedCampaign) => void;
  onExtend?: (campaign: IUnifiedCampaign) => void;
  onViewHistory?: (campaign: IUnifiedCampaign) => void;
  onEnd?: (campaign: IUnifiedCampaign) => void;
}

export function getUnifiedCampaignsColumns({
  onViewDetails,
  onEdit,
  onViewProducts,
  onExtend,
  onViewHistory,
  onEnd,
}: GetColumnsProps): ColumnDef<IUnifiedCampaign>[] {
  return [
    {
      accessorKey: "type",
      header: "Tipo",
      cell: ({ row }) => <CampaignTypeBadge type={row.getValue("type")} />,
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: "code",
      header: ({ column }) => {
        return (
          <Button
            variant="outline"
            className="border-none"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Código
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="font-mono text-sm">#{row.getValue("code")}</div>
      ),
    },
    {
      accessorKey: "description",
      header: "Descrição",
      cell: ({ row }) => {
        const description = row.getValue("description") as string;
        return (
          <div className="max-w-[300px]">
            <p className="font-medium truncate">{description}</p>
            <p className="text-xs text-muted-foreground truncate">
              {row.original.configSummary}
            </p>
          </div>
        );
      },
    },
    {
      accessorKey: "storeName",
      header: "Loja",
      cell: ({ row }) => {
        const storeName = row.getValue("storeName") as string;
        const storeCode = row.original.storeCode;

        // Para "Todas as lojas" (999), exibe sem tooltip
        if (storeCode === 999) {
          return (
            <div className="flex items-center gap-1.5">
              <Store className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-medium text-sm">{storeName}</span>
            </div>
          );
        }

        // Para lojas específicas, exibe com tooltip
        return (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1.5 cursor-help">
                  <Store className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium text-sm underline decoration-dotted">
                    {storeName}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start">
                <div className="text-sm">
                  <p className="font-medium">{storeName}</p>
                  <p className="text-muted-foreground">Código: {storeCode}</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: "startDate",
      header: ({ column }) => {
        return (
          <Button
            variant="outline"
            className="border-none"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Data Início
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const date = new Date(row.getValue("startDate"));
        return (
          <div className="text-sm">
            {format(date, "dd/MM/yyyy", { locale: ptBR })}
          </div>
        );
      },
    },
    {
      accessorKey: "endDate",
      header: ({ column }) => {
        return (
          <Button
            variant="outline"
            className="border-none"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Data Término
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const date = new Date(row.getValue("endDate"));
        return (
          <div className="text-sm">
            {format(date, "dd/MM/yyyy", { locale: ptBR })}
          </div>
        );
      },
    },
    {
      accessorKey: "visualStatus",
      header: "Status",
      cell: ({ row }) => {
        return <CampaignStatusBadge status={row.getValue("visualStatus")} />;
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <UnifiedCampaignRowActions
          row={row}
          onViewDetails={onViewDetails}
          onEdit={onEdit}
          onViewProducts={onViewProducts}
          onExtend={onExtend}
          onViewHistory={onViewHistory}
          onEnd={onEnd}
        />
      ),
    },
  ];
}
